import { useEffect, useMemo, useState } from "react";

import { Box, Grid, Table } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { Button, EmptyMessage, Loader, Pagination, TextInput } from "components";
import { IRootState } from "config/redux";
import { AuthorityConstants, ServiceConstants } from "constant";
import { useListHook } from "hooks";
import { tableClasses } from "styles";
import { ClientListResponse } from "types";
import { AppUtils, UserUtils } from "utils";

import ClientListItem from "./item";

const { ROLE_CR_R } = AuthorityConstants;
const { REQUEST_PENDING, REQUEST_USERS_CLIENTS_ID } = ServiceConstants;
interface ClientsListProps {
  phase: string;
  fetch: (page: number, search: string | undefined, phase: string, source: string) => void;
  addNew?: () => void;
}

let searchTimeout: NodeJS.Timeout | undefined = undefined;

const ClientsList = ({ addNew, phase, fetch }: ClientsListProps) => {
  const { t } = useTranslation();
  const serviceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_USERS_CLIENTS_ID],
  );
  const { page, content, updatePageable, resetList, totalPages, refreshList, setPage } =
    useListHook();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, REQUEST_USERS_CLIENTS_ID, (val) => {
      resetList();
      updatePageable(val);
    });
  }, [serviceObject]);

  useEffect(() => {
    if (page === undefined) {
      return;
    }
    if (page === -1) {
      resetList();
      return;
    }
    fetch(page, debouncedSearch, phase, searchParams.get("source") ?? "");
  }, [page, searchParams]);

  useEffect(() => {
    if (debouncedSearch === undefined) {
      return;
    }
    const length = debouncedSearch.trim().length;
    if (length > 0 && length < 3) {
      return;
    }
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(refreshList, 500);
  }, [debouncedSearch]);

  const isLoading = useMemo(
    () => serviceObject && serviceObject.status === REQUEST_PENDING,
    [serviceObject],
  );

  const rows = useMemo(() => {
    if (!content) {
      return <></>;
    }
    return content.map((el: ClientListResponse) => (
      <ClientListItem key={el.user.id} item={el} t={t} />
    ));
  }, [content, t]);

  return (
    <Box key={`list-${phase}`}>
      <Grid justify={"flex-end"}>
        {addNew && (
          <Grid.Col span={{ sm: 1, md: 4, lg: 8 }}>
            <Button onClick={addNew} size={"sm"}>
              <IconPlus />
            </Button>
          </Grid.Col>
        )}
        <Grid.Col span={{ sm: addNew ? 11 : 12, md: 8, lg: 4 }}>
          <TextInput
            placeholder={t("search.all")}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
        </Grid.Col>
      </Grid>
      <Box pos={"relative"}>
        <Loader overlay={true} visible={content === undefined || isLoading} />
        {content &&
          (content.length === 0 ? (
            <EmptyMessage message={t("clients.list.empty")} />
          ) : (
            <>
              <Table className={tableClasses.table}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("user.fullName.label")}</Table.Th>
                    <Table.Th visibleFrom={"md"}>{t("user.gender.label")}</Table.Th>
                    <Table.Th>{t("user.phone.label")}</Table.Th>
                    <Table.Th visibleFrom={"md"}>{t("credit.partnerLastName.short")}</Table.Th>
                    <Table.Th visibleFrom={"md"}>{t("credit.partnerPhoneNumber.short")}</Table.Th>
                    <Table.Th visibleFrom={"md"}>{t("user.address.city.label")}</Table.Th>
                    <Table.Th>{t("source.title")}</Table.Th>
                    <Table.Th>{t("user.acquisition.label")}</Table.Th>
                    <Table.Th visibleFrom={"md"} colSpan={2}>
                      {t("clients.date.change.label")}
                    </Table.Th>
                    <Table.Th>{t("credit.status.label")}</Table.Th>
                    <Table.Th ta={"center"}>{t("credit.comment.title")}</Table.Th>
                    {UserUtils.hasAuthority(ROLE_CR_R) && (
                      <Table.Th ta={"center"}>{t("link.title")}</Table.Th>
                    )}
                    <Table.Th>{t("credit.agent.label")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{rows}</Table.Tbody>
              </Table>
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </>
          ))}
      </Box>
    </Box>
  );
};

export default ClientsList;
